/** @jsxImportSource @emotion/react */

import * as React from 'react'
import styled from '@emotion/styled'
import { css, jsx } from '@emotion/react'
import { spacing, Primary, mixins } from '@sphinx/components-styles'
import { ChevronDownIcon } from '../icons'

export interface AccordionProps {
  disabled?: boolean
  expanded?: boolean
  children: React.ReactNode
}

enum Position {
  end = 0,
  start = -1,
}

interface AccordionHeaderProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  iconPosition?: keyof typeof Position
}

interface AccordionContextProps {
  isOpen: boolean
  disabled: boolean
  toggle: () => void
}

export const AccordionContext = React.createContext<AccordionContextProps>({
  isOpen: false,
  disabled: false,
  toggle: () => undefined,
})

export const Accordion: React.FC<AccordionProps> = ({
  disabled = false,
  expanded = false,
  children,
}) => {
  const [isOpen, setOpen] = React.useState(expanded)

  const toggle = () => !disabled && setOpen(!isOpen)

  const contextValue = React.useMemo(
    () => ({ isOpen, disabled, toggle }),
    [isOpen, disabled, toggle]
  )
  return (
    <AccordionContext.Provider value={contextValue}>
      {children}
    </AccordionContext.Provider>
  )
}

export const AccordionHeader: React.FC<AccordionHeaderProps> = ({
  children,
  iconPosition = 'end',
  ...props
}) => {
  const ref = React.useRef(null)
  const { disabled, toggle, isOpen } = React.useContext(AccordionContext)

  const onMouseUpHandle = (e: React.MouseEvent<HTMLButtonElement>) => {
    ref.current.blur()

    if (props.onMouseUp) {
      props.onMouseUp(e)
    }
  }
  const onClickHandle = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (props.onClick) {
      props.onClick(e)
    }
    toggle()
  }

  return (
    <HeaderWrap
      {...props}
      ref={ref}
      disabled={disabled}
      onMouseUp={onMouseUpHandle}
      onClick={onClickHandle}
    >
      {children && <HeaderContent isOpen={isOpen}>{children}</HeaderContent>}
      <ToggleIcon
        role="toggle"
        position={Position[iconPosition]}
        isOpen={isOpen}
      >
        <ChevronDownIcon />
      </ToggleIcon>
    </HeaderWrap>
  )
}

export const AccordionDetails: React.FC<
  React.HTMLAttributes<HTMLDivElement>
> = ({ children, ...props }) => {
  const { isOpen } = React.useContext(AccordionContext)
  return (
    <Details {...props} isOpen={isOpen}>
      {children}
    </Details>
  )
}

const HeaderWrap = styled.button`
  ${mixins.body}
  box-sizing: border-box;
  border: 2px solid transparent;
  background: ${Primary.neutral.white};
  color: ${Primary.navy[100]};
  display: flex;
  width: 100%;
  align-items: center;
  min-height: 44px;
  padding: 0 ${spacing.xxs};
  outline: none;
  cursor: pointer;

  &:disabled {
    pointer-events: none;

    & * {
      color: ${Primary.navy[20]};
    }
    & path {
      fill: ${Primary.navy[20]};
    }
  }
  &:focus {
    border: 2px solid ${Primary.orange[100]};
  }
  &:hover {
    background: ${Primary.orange[10]};
  }
  & [role='toggle']:only-child {
    margin: 0;
  }
`

const HeaderContent = styled.div<{ isOpen: boolean }>`
  flex-grow: 1;
  display: flex;
  color: ${({ isOpen }) => isOpen && Primary.orange[100]};
`

const ToggleIcon = styled.div<{ isOpen: boolean; position: Position }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 auto;
  transition: 200ms ease-in-out;
  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: rotate(180deg);
      & path {
        fill: ${Primary.orange[100]};
      }
    `}
  order: ${({ position }) => position};
  ${({ position }) =>
    position === Position.start
      ? css`
          margin-right: 8px;
        `
      : css`
          margin-left: 8px;
        `}
`

const Details = styled.div<{ isOpen: boolean }>`
  ${mixins.body}
  box-sizing: border-box;
  background: ${Primary.neutral.white};
  color: ${Primary.navy[100]};
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: ${spacing.xxs} ${spacing.xxs} ${spacing.xs} ${spacing.xxs};
  overflow: visible;

  ${({ isOpen }) =>
    !isOpen &&
    css`
      height: 0;
      padding: 0;
      min-height: 0;
      overflow: hidden;
    `}
`
